import { FC, useState } from "react";

import { isEmail } from "validator";

import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { FormButton } from "@/forms-design-system/FormButton";
import { FormDialog } from "@/forms-design-system/FormDialog";
import { FormTextField } from "@/forms-design-system/FormTextField";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { Success, User } from "@/icons/outlined";

export interface BuyerLoginDialogCopy {
  title: string;
  description: string;
  emailLabel: string;
  emailPlaceholder: string;
  sendLabel: string;
  cancelLabel: string;
  closeLabel: string;
  successTitle: string;
  successDescription: string;
  invalidEmailMessage: string;
}

interface BuyerLoginDialogProps {
  open: boolean;
  onClose: () => void;
  onSendLink: (email: string) => Promise<string | null>;
  isPending: boolean;
  copy: BuyerLoginDialogCopy;
  dataTestPrefix?: string;
}

export const BuyerLoginDialog: FC<BuyerLoginDialogProps> = ({
  open,
  onClose,
  onSendLink,
  isPending,
  copy,
  dataTestPrefix = "buyer-login-dialog",
}) => {
  const { isSmallScreen } = useMediaQuery();
  const [email, setEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleClose = () => {
    onClose();
    setEmail("");
    setEmailSent(false);
    setErrorMessage(null);
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    setErrorMessage(null);
  };

  const handleSubmit = async () => {
    if (!isEmail(email)) {
      setErrorMessage(copy.invalidEmailMessage);
      return;
    }

    setErrorMessage(null);
    const sendErrorMessage = await onSendLink(email);

    if (sendErrorMessage) {
      setErrorMessage(sendErrorMessage);
      return;
    }

    setEmailSent(true);
  };

  if (emailSent) {
    return (
      <FormDialog
        open={open}
        onClose={handleClose}
        size="xs"
        icon={<Success />}
        variant={isSmallScreen ? "drawer" : "default"}
        title={<span data-test={`${dataTestPrefix}-success-title`}>{copy.successTitle}</span>}
        actions={[
          <FormButton key="close" variant="tonal" onClick={handleClose}>
            {copy.closeLabel}
          </FormButton>,
        ]}
      >
        <FormTypography variant="body2">{copy.successDescription}</FormTypography>
      </FormDialog>
    );
  }

  return (
    <FormDialog
      open={open}
      onClose={handleClose}
      size="xs"
      icon={<User />}
      variant={isSmallScreen ? "drawer" : "default"}
      title={<span data-test={`${dataTestPrefix}-title`}>{copy.title}</span>}
      actions={[
        <FormButton
          key="submit"
          variant="solid"
          onClick={handleSubmit}
          disabled={!email || isPending}
          data-test={`${dataTestPrefix}-submit`}
        >
          {copy.sendLabel}
        </FormButton>,
        <FormButton key="cancel" variant="outline" onClick={handleClose} data-test={`${dataTestPrefix}-cancel`}>
          {copy.cancelLabel}
        </FormButton>,
      ]}
    >
      <FormTypography variant="body2">{copy.description}</FormTypography>
      <FormTextField
        label={copy.emailLabel}
        placeholder={copy.emailPlaceholder}
        type="email"
        value={email}
        onChange={(e) => handleEmailChange(e.target.value)}
        error={Boolean(errorMessage)}
        helperText={errorMessage ?? undefined}
        data-test={`${dataTestPrefix}-email`}
      />
    </FormDialog>
  );
};
